export const PRODUCT_COUNT_FOR_LOW_STOCK = 5;

export const REFRESH_POLL_INTERVAL_FOR_NEW_RESPONSE = 30_000;

export const RESPONSES_QUEUE_LIMIT = 50;

export const DEFAULT_PERSONA = 'Charlotte';

export const DEFAULT_MESSAGE_TEXT = '{shop_name}: ';
// 90px is the height of the header and footer
export const MAX_HEIGHT = 'calc(100vh - 90px)';

export const BASE_URL = '/workspace';

export const SYSTEM_EVENT_SALES_SUBSTRING = 'convert_event';
export const HANDOFF_EVENT_TYPE_PREFIX = 'convert_event_handoff';
export const SYSTEM_EVENT_SALES_AI_SUBSTRING = 'convert_event_sales_ai';
export const SYSTEM_EVENT_SALES_AI_CLASSIFICATION =
  'convert_event_sales_ai/classification';
export const SYSTEM_EVENT_SALES_AI_FETCH = 'convert_event_sales_ai/fetch';
export const SUBSCRIBER_EVENT_SALES_LINK_CLICK =
  'convert_event_subscriber/link_click';

export const TIMER_STATE_KEY = 'chatTimerState';
export const SUBSCRIBER_EVENTS = [SUBSCRIBER_EVENT_SALES_LINK_CLICK];

export const VIEW_OPTIONS = {
  MERCHANT: 'Merchant View',
  SALES_ASSOCIATE: 'Sales Associate View',
  AI_DEBUGGER: 'Bub AI Debugger View',
} as const;

export type ViewOptions = typeof VIEW_OPTIONS[keyof typeof VIEW_OPTIONS];

export const MESSAGE_SUBTYPES = {
  SHOPPER: 'ssai',
} as const;

export const isShopperSubtype = (subtype?: string | null): boolean =>
  !!subtype && subtype.startsWith(MESSAGE_SUBTYPES.SHOPPER);

/**
 * Subscriber-activity events: what the *subscriber* did — browsed, checked
 * out, ordered, searched, opted out. Distinct from the internal AI and
 * support-routing chatter below, which describes what the *system* did.
 *
 * Two API sources feed these, with unrelated type prefixes:
 *  - `profile.{event_type}` — marketing_ai/sender/queries/events (ALLOWED_EVENTS).
 *  - `convert_event_{name}` — the SMS Sales event stream
 *    (sms_sales/queries/event_stream.py), emitted by shopify_webhook_helper.py
 *    and sms_sales/events/dynamodb.py.
 *
 * Keyed by the Conversations "Events" filter each type belongs to, so the
 * filter and the merchant-visibility gate in utils/roles.tsx cannot drift
 * apart. Note there is no checkout event on the `profile.*` side — a checkout
 * only ever arrives as `convert_event_checkout/create`.
 */
export const SUBSCRIBER_ACTIVITY_EVENT_TYPES = {
  'opt-out-events': [
    'profile.verification.consents.opt_out',
    'convert_event_subscriber/opt_out',
  ],
  'add-to-cart': ['profile.subscriber.product.added_to_cart'],
  'viewed-product': ['profile.subscriber.product.browsed'],
  'created-order': [
    'profile.subscriber.order.created',
    'convert_event_order/create',
  ],
  'started-checkout': ['convert_event_checkout/create'],
  'subscriber-search': ['convert_event_subscriber/searched'],
};

/** Flattened form of SUBSCRIBER_ACTIVITY_EVENT_TYPES. */
export const ALL_SUBSCRIBER_ACTIVITY_EVENT_TYPES: readonly string[] =
  Object.values(SUBSCRIBER_ACTIVITY_EVENT_TYPES).flat();

/**
 * True for events describing what the subscriber did — checkout, order,
 * search, opt-out — as opposed to internal AI or support-routing chatter.
 *
 * Lives here rather than in utils/roles so the Responses timeline can use it
 * without a circular import (roles imports isHandoffEvent from that module).
 */
export const isSubscriberActivityEvent = (message: {
  type?: string;
}): boolean =>
  !!message?.type && ALL_SUBSCRIBER_ACTIVITY_EVENT_TYPES.includes(message.type);

export const SUBSCRIBER_TIMELINE_EVENTS = {
  HANDOFF_CONFIRMATION_PROMPT_SENT:
    'convert_event_handoff:confirmation_prompt_sent',
  HANDOFF_CONFIRMATION_PROMPT_FAILED:
    'convert_event_handoff:confirmation_prompt_failed',
  HANDOFF_CONFIRMATION_ACCEPTED: 'convert_event_handoff:confirmation_accepted',
  HANDOFF_CONFIRMATION_DECLINED: 'convert_event_handoff:confirmation_declined',
  HANDOFF_CONFIRMATION_TIMEOUT: 'convert_event_handoff:confirmation_timeout',
  HANDOFF_ESCALATED: 'convert_event_handoff:escalated',
  HANDOFF_TICKET_CREATED: 'convert_event_handoff:ticket_created',
  HANDOFF_TICKET_FAILED: 'convert_event_handoff:ticket_failed',
  HANDOFF_TICKET_CLOSED: 'convert_event_handoff:ticket_closed',
  HANDOFF_TIMEOUT: 'convert_event_handoff:timeout',
  HANDOFF_SHOPPER_RESUMED: 'convert_event_handoff:shopper_resumed',
  ROUTED_TO_GORGIAS: 'convert_event_Conversation routed to gorgias.',
  CONTINUING_TO_GORGIAS:
    'convert_event_Continuing to route messages to Gorgias.',
  GORGIAS_TICKET_AUTO_CLOSED: 'convert_event_gorgias/ticket_auto_closed',
  ROUTED_TO_ZENDESK: 'convert_event_Conversation routed to Zendesk.',
  CONTINUING_TO_ZENDESK:
    'convert_event_Continuing to route messages to Zendesk.',
  ZENDESK_CONVERSATION_CLOSED: 'convert_event_Zendesk conversation closed.',
  PUSHED_TO_ZENDESK: 'convert_event_support/pushed_to_zendesk',
  ROUTED_TO_SUPPORT_EMAIL:
    'convert_event_Conversation routed to support email.',
} as const;

export type SubscriberTimelineEvent =
  typeof SUBSCRIBER_TIMELINE_EVENTS[keyof typeof SUBSCRIBER_TIMELINE_EVENTS];

export const SUPPORT_INTEGRATION_LABELS = {
  gorgias: 'Gorgias',
  zendesk: 'Zendesk',
  api_request: 'Partner API',
  'klaviyo-form': 'Klaviyo',
  'shopify-checkout': 'Shopify Checkout',
  '3rd-party-onsite-opt-in-alia': 'Alia',
} as const;

export type SupportIntegrationSubtype = keyof typeof SUPPORT_INTEGRATION_LABELS;

export const getSupportIntegrationLabel = (
  subtype?: string | null,
): string | undefined => {
  if (!subtype) return undefined;
  return SUPPORT_INTEGRATION_LABELS[subtype as SupportIntegrationSubtype];
};
